<script setup>
import { onBeforeUnmount, onMounted, ref } from "vue";
import MapStyleSelector from "../../../../maps/MapStyleSelector.vue";
import MapZoomControls from "../../../../maps/MapZoomControls.vue";
const props = defineProps({
  isLoadingMap: {
    type: Boolean,
    default: false,
  },
  initError: {
    type: String,
    default: "",
  },
  isTimelineExpanded: {
    type: Boolean,
    default: false,
  },
  isTimelineVisible: {
    type: Boolean,
    default: true,
  },
  mapStyle: {
    type: String,
    default: "",
  },
  timelineHeight: {
    type: Number,
    default: 172,
  },
});

const emit = defineEmits(["timeline-visibility-change", "zoom-in", "zoom-out", "fit", "style-change"]);
const isTrafficEnabled = ref(false);

let trafficCheckboxEl = null;
let trafficToggleBtnEl = null;
let syncTrafficStateHandler = null;

function setTimelineVisible(value) {
  emit("timeline-visibility-change", Boolean(value));
}

function toggleTimelineVisibility() {
  setTimelineVisible(!props.isTimelineVisible);
}

function syncTrafficState() {
  isTrafficEnabled.value = Boolean(trafficCheckboxEl?.checked);
}

onMounted(() => {
  trafficCheckboxEl = document.getElementById("showTraffic");
  trafficToggleBtnEl = document.getElementById("trafficToggleBtn");

  syncTrafficStateHandler = () => {
    requestAnimationFrame(() => {
      syncTrafficState();
    });
  };

  syncTrafficState();
  if (trafficCheckboxEl) {
    trafficCheckboxEl.addEventListener("change", syncTrafficStateHandler);
  }
  if (trafficToggleBtnEl) {
    trafficToggleBtnEl.addEventListener("click", syncTrafficStateHandler);
  }
});

onBeforeUnmount(() => {
  if (trafficCheckboxEl && syncTrafficStateHandler) {
    trafficCheckboxEl.removeEventListener("change", syncTrafficStateHandler);
  }
  if (trafficToggleBtnEl && syncTrafficStateHandler) {
    trafficToggleBtnEl.removeEventListener("click", syncTrafficStateHandler);
  }
  trafficCheckboxEl = null;
  trafficToggleBtnEl = null;
  syncTrafficStateHandler = null;
});
</script>

<template>
  <div class="plan-map-hidden-controls" aria-hidden="true">
    <input
      id="subscriptionKey"
      type="hidden"
      placeholder="Se rellena desde local.settings si existe"
    >
    <input id="showTraffic" type="checkbox">
    <input id="autoFit" type="checkbox" checked>
    <textarea id="contractInput" spellcheck="false"></textarea>
    <button id="loadSample" class="secondary" type="button">Cargar sample</button>
    <button id="formatJson" class="secondary" type="button">Formatear JSON</button>
    <button id="clearMap" class="danger" type="button">Limpiar mapa</button>
    <button id="renderMap" class="primary" type="button">Renderizar rutas</button>
    <button id="fitMap" class="secondary" type="button">Recentrar</button>
    <button id="showAllRoutes" class="secondary" type="button">Mostrar todas</button>
    <button id="hideAllRoutes" class="secondary" type="button">Ocultar todas</button>
    <div id="routeList" class="route-list"></div>
    <div id="statusBox" class="status" data-tone="info">
      <div>
        <strong>Esperando datos</strong>
        <p id="statusText" class="status-meta">Inicializando vista de rutas y contrato de ejemplo.</p>
      </div>
      <span id="cacheBadge" class="status-meta">cache vacia</span>
    </div>
  </div>

  <div
    class="map-content"
    :class="{ 'timeline-hidden': !props.isTimelineVisible }"
    :style="props.isTimelineVisible ? { '--timeline-height': props.timelineHeight + 'px' } : {}"
  >
    <div class="map-stage">
      <div class="map-overlay-controls">
        <div class="map-control-group map-control-group--traffic">
          <button
            id="trafficToggleBtn"
            v-tooltip="isTrafficEnabled ? 'Ocultar trafico' : 'Mostrar trafico'"
            class="map-control-btn traffic-btn"
            type="button"
            aria-label="Activar o desactivar trafico"
          >
            Tráfico
          </button>
          <button
            id="timelineToggleBtn"
            v-tooltip="props.isTimelineVisible ? 'Ocultar time line' : 'Mostrar timeline'"
            class="map-control-btn traffic-btn timeline-toggle-btn"
            :class="{ 'is-active': props.isTimelineVisible }"
            type="button"
            :aria-label="props.isTimelineVisible ? 'Ocultar time line' : 'Mostrar timeline'"
            @click="toggleTimelineVisibility"
          >
            <svg viewBox="0 0 24 24" class="timeline-toggle-icon" aria-hidden="true">
              <line x1="6" y1="7.5" x2="18" y2="7.5"></line>
              <line x1="6" y1="12" x2="15.5" y2="12"></line>
              <line x1="6" y1="16.5" x2="17" y2="16.5"></line>
            </svg>
          </button>
        </div>
        <div class="map-control-group map-control-group--zoom">
          <map-style-selector
            :model-value="props.mapStyle"
            @change="emit('style-change', $event)"
          />
          <map-zoom-controls
            @zoom-in="emit('zoom-in')"
            @zoom-out="emit('zoom-out')"
            @fit="emit('fit')"
          />
        </div>
      </div>

      <div id="map" role="application" aria-label="Mapa de rutas del plan"></div>

      <div v-if="props.isLoadingMap" class="map-loading-overlay">
        <div class="spinner-border spinner-border-sm text-primary me-2" role="status" aria-hidden="true"></div>
       
      </div>

      <div v-if="props.initError" class="map-error-overlay">
        {{ props.initError }}
      </div>
    </div>

    <slot />
  </div>
</template>

<style scoped lang="scss">
@import "../../../../maps/map-overlay-controls";

.plan-map-hidden-controls {
  display: none;
}

.map-content {
  flex: 1 1 auto;
  height: 100%;
  min-height: 0;
  position: relative;
  width: 100%;
}

.map-stage {
  background: #f8fafc;
  height: 100%;
  min-height: 0;
  overflow: hidden;
  position: relative;
  width: 100%;
}

#map {
  border-bottom-left-radius: 14px;
  border-bottom-right-radius: 14px;
  height: 100%;
  min-height: 0;
  overflow: hidden;
  width: 100%;
}

.map-control-group--traffic {
  left: 14px;
  top: 14px;
}

.map-control-group--zoom {
  align-items: center;
  bottom: calc(var(--timeline-height, 172px) + 12px);
  flex-direction: column;
  right: 14px;
}

.map-content.timeline-hidden .map-control-group--zoom {
  align-items: center;
  bottom: 14px;
  flex-direction: column;
}

.map-control-btn.traffic-btn.is-active-pulse {
}

.map-control-btn.timeline-toggle-btn {
  min-width: 34px;
  padding: 0;
  width: 34px;
}

.timeline-toggle-icon {
  fill: none;
  height: 13px;
  opacity: 0.68;
  stroke: currentColor;
  stroke-linecap: round;
  stroke-linejoin: round;
  stroke-width: 1.7;
  width: 13px;
}

.timeline-toggle-btn:hover .timeline-toggle-icon,
.timeline-toggle-btn.is-active .timeline-toggle-icon {
  opacity: 0.92;
}

.map-control-btn.fit-btn {
  font-size: 14px;
}

@keyframes traffic-toggle-pulse {
  0% {
    transform: scale(1);
  }

  45% {
    transform: scale(1.08);
  }

  100% {
    transform: scale(1);
  }
}

:deep(.marker-origin),
:deep(.marker-stop) {
  align-items: center;
  color: #fff;
  display: flex;
  font-weight: 700;
  justify-content: center;
  position: relative;
  user-select: none;
}

:deep(.marker-origin.has-warning),
:deep(.marker-stop.has-warning) {
  border-color: rgba(251, 191, 36, 0.98);
  box-shadow:
    0 0 0 3px rgba(251, 191, 36, 0.26),
    0 0 16px rgba(251, 191, 36, 0.62),
    0 10px 20px rgba(0, 0, 0, 0.18);
}

:deep(.marker-origin) {
  border: 2px solid rgba(255, 255, 255, 0.96);
  border-radius: 50%;
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.18);
  height: 26px;
  transform: translate(-50%, -100%);
  width: 26px;
}

:deep(.marker-origin)::after {
  background: inherit;
  border-bottom: 2px solid rgba(255, 255, 255, 0.96);
  border-right: 2px solid rgba(255, 255, 255, 0.96);
  bottom: -5px;
  content: "";
  height: 8px;
  left: 50%;
  position: absolute;
  transform: translateX(-50%) rotate(45deg);
  width: 8px;
  z-index: -1;
}

:deep(.marker-origin.has-warning)::after {
  border-bottom-color: rgba(251, 191, 36, 0.98);
  border-right-color: rgba(251, 191, 36, 0.98);
}

:deep(.marker-origin.marker-destination:not(.has-warning)) {
  border-color: currentColor;
}

:deep(.marker-origin.marker-destination:not(.has-warning))::after {
  border-bottom-color: currentColor;
  border-right-color: currentColor;
}

:deep(.marker-origin > .marker-origin-label) {
  align-items: center;
  display: flex;
  font-size: 10px;
  inset: 0;
  justify-content: center;
  line-height: 1;
  position: absolute;
}

:deep(.marker-stop) {
  border: 2px solid rgba(255, 255, 255, 0.96);
  border-radius: 50%;
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.15);
  font-size: 11px;
  height: 26px;
  transform: translate(-50%, -50%);
  width: 26px;
}

@media (max-width: 1200px) {
  .map-control-group--zoom {
    bottom: calc(var(--timeline-height, 160px) + 12px);
  }

  .map-content.timeline-hidden .map-control-group--zoom {
    bottom: 12px;
  }

}
</style>
